import type { UserManagerSettings } from 'oidc-client-ts';
import { createChromeStorageStateStore } from './chromeStorageStateStore';

const isExtension = typeof __IN_EXTENSION__ !== 'undefined' && __IN_EXTENSION__;

/** In extension: native callback page for OAuth. In web: origin for path-based routes. */
function getRedirectBase(): string {
    if (isExtension && typeof chrome !== 'undefined' && chrome.runtime?.getURL) {
        return chrome.runtime.getURL('app/auth/callback.html');
    }
    return window.location.origin;
}

/** In extension: app URL for post-logout redirect. In web: origin + /logout. */
function getPostLogoutRedirectUri(): string {
    if (isExtension && typeof chrome !== 'undefined' && chrome.runtime?.getURL) {
        return chrome.runtime.getURL('app/index.html');
    }
    return `${window.location.origin}/logout`;
}

function getRequiredEnv(key: string): string {
    const value = import.meta.env[key] as string | undefined;
    if (!value) throw new Error(`Missing required env var: ${key}`);
    return value;
}

/**
 * When VITE_COGNITO_DOMAIN is set (e.g. id.copilot.warrcloud.io), we override
 * every Hosted-UI-served endpoint so the OIDC client never reaches the default
 * `*.auth.<region>.amazoncognito.com` prefix domain. Cognito drops that prefix
 * domain when a custom domain is provisioned, so leaving any of these endpoints
 * on the discovered (non-overridden) URL causes DNS_PROBE_FINISHED_NXDOMAIN at
 * token exchange / userinfo time.
 *
 * Discovery still uses the cognito-idp authority for keys, issuer, etc.;
 * metadataSeed overrides only the user-facing endpoints.
 */
function getMetadataSeed():
    | Partial<{
          authorization_endpoint: string;
          token_endpoint: string;
          userinfo_endpoint: string;
          end_session_endpoint: string;
          revocation_endpoint: string;
      }>
    | undefined {
    const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
    if (!domain || !domain.trim()) return undefined;
    const base = domain.startsWith('http') ? domain.replace(/\/+$/, '') : `https://${domain}`;
    return {
        authorization_endpoint: `${base}/oauth2/authorize`,
        token_endpoint: `${base}/oauth2/token`,
        userinfo_endpoint: `${base}/oauth2/userInfo`,
        end_session_endpoint: `${base}/logout`,
        revocation_endpoint: `${base}/oauth2/revoke`,
    };
}

export function getOidcConfig(): UserManagerSettings {
    const region = getRequiredEnv('VITE_COGNITO_REGION');
    const userPoolId = getRequiredEnv('VITE_COGNITO_USER_POOL_ID');
    const clientId = getRequiredEnv('VITE_COGNITO_CLIENT_ID');
    const scopes =
        (import.meta.env.VITE_COGNITO_SCOPES as string | undefined) ?? 'openid email profile';

    const base = getRedirectBase();
    const redirect_uri = isExtension ? base : `${base}/callback`;
    const post_logout_redirect_uri = getPostLogoutRedirectUri();

    const metadataSeed = getMetadataSeed();

    return {
        authority: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
        client_id: clientId,
        redirect_uri,
        post_logout_redirect_uri,
        response_type: 'code',
        scope: scopes,
        automaticSilentRenew: true,
        ...(metadataSeed && { metadataSeed }),
        ...(isExtension && { stateStore: createChromeStorageStateStore() }),
    };
}
